/**
 * Style1 (Modern Grid) / Fluid Stack — Style-specific sections for GridEngine.
 *
 * hero_carousel: HeroCarousel with 3 animated gradient slides + charity tagline
 * SharedLiveDrawSection: LiveDrawCard wrapper with loading/empty states (reused by style2-4, fluid)
 */
import React, { useState } from 'react'
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import HeroCarousel from '../../components/sections/HeroCarousel'
import LiveDrawCard from '../../components/LiveDrawCard'
import ShareModal from '../../components/ShareModal'
import InlineTranslationEdit from '../../components/InlineTranslationEdit'
import HeroPartnerGlassSelect from '../../components/HeroPartnerGlassSelect'
import { useContent } from '../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../hooks/usePurchaseNavigate'

/**
 * Style1 / Fluid hero carousel — 3 animated gradient slides with icons.
 * Mirrors Classic5050Style1.jsx lines 46-103.
 */
export function Style1HeroCarouselSection({ partner, mainDraw, t, editMode, handleTranslationUpdate, onLoginClick }) {
  const { c } = useContent()

  const slides = [
    {
      title: <InlineTranslationEdit translationKey="classic5050.heroTitle" value={c('classic5050.heroTitle', t?.('classic5050.heroTitle'))} fallback="Win Big, Give Back" onUpdate={handleTranslationUpdate} editMode={editMode} />,
      subtitle: <InlineTranslationEdit translationKey="classic5050.heroSubtitle" value={c('classic5050.heroSubtitle', t?.('classic5050.heroSubtitle'))} fallback="Play weekly for a chance at winning half the pot" onUpdate={handleTranslationUpdate} editMode={editMode} />,
      bgGradient: 'from-blue-500 via-cyan-500 to-teal-400',
      icon: DollarSign,
      stats: <InlineTranslationEdit translationKey="classic5050.splitLabel" value={c('classic5050.splitLabel', t?.('classic5050.splitLabel'))} fallback="50% to Winner, 50% to Charity" onUpdate={handleTranslationUpdate} editMode={editMode} />,
    },
    {
      title: <InlineTranslationEdit translationKey="classic5050.charityTitle" value={c('classic5050.charityTitle', t?.('classic5050.charityTitle'))} fallback="Empowering Youth & Families" onUpdate={handleTranslationUpdate} editMode={editMode} />,
      subtitle: <InlineTranslationEdit translationKey="classic5050.charityDesc" value={c('classic5050.charityDesc', t?.('classic5050.charityDesc'))} fallback="Every ticket supports programs in our community" onUpdate={handleTranslationUpdate} editMode={editMode} />,
      bgGradient: 'from-red-500 via-rose-500 to-pink-500',
      icon: Heart,
      stats: <InlineTranslationEdit translationKey="classic5050.charityImpact" value={c('classic5050.charityImpact', t?.('classic5050.charityImpact'))} fallback="Over $500K raised for community programs" onUpdate={handleTranslationUpdate} editMode={editMode} />,
    },
    {
      title: <InlineTranslationEdit translationKey="classic5050.joinTitle" value={c('classic5050.joinTitle', t?.('classic5050.joinTitle'))} fallback="Join Thousands of Players" onUpdate={handleTranslationUpdate} editMode={editMode} />,
      subtitle: <InlineTranslationEdit translationKey="classic5050.joinDesc" value={c('classic5050.joinDesc', t?.('classic5050.joinDesc'))} fallback="Be part of something bigger - win while making a difference" onUpdate={handleTranslationUpdate} editMode={editMode} />,
      bgGradient: 'from-emerald-500 via-green-500 to-teal-500',
      icon: Users,
      stats: <InlineTranslationEdit translationKey="classic5050.joinStat" value={c('classic5050.joinStat', t?.('classic5050.joinStat'))} fallback="10,000+ active players supporting our charity" onUpdate={handleTranslationUpdate} editMode={editMode} />,
    },
  ]

  return (
    <div>
      <HeroCarousel
        slides={slides}
        drawDate={mainDraw}
        charityName={partner?.partner_name || 'YMCA'}
        charityTagline={
          <InlineTranslationEdit
            translationKey="classic5050.footerTagline"
            value={c('classic5050.footerTagline', t?.('classic5050.footerTagline'))}
            fallback="Building stronger communities together"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        }
      />
      <HeroPartnerGlassSelect className="mx-auto mt-4 w-full max-w-md" />
    </div>
  )
}

/**
 * Shared live_draw section — LiveDrawCard wrapper with loading/empty states.
 * Used by style1, style2, style3, style4, fluid.
 * Mirrors Classic5050Style1.jsx lines 114-143.
 */
export function SharedLiveDrawSection({ mainDraw, loading, onLoginClick, bonusDraws, editMode, handleTranslationUpdate, gameType }) {
  const { c } = useContent()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const [shareModalOpen, setShareModalOpen] = useState(false)

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700 h-96 flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">{c('HomeStyle1LoadingText', 'Loading draws...')}</div>
      </div>
    )
  }

  if (!mainDraw) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-gray-600 dark:text-gray-400 text-center">
          <InlineTranslationEdit
            translationKey="HomeStyle1NoActiveDraws"
            value={c('HomeStyle1NoActiveDraws', 'No active draws at the moment.')}
            fallback="No active draws at the moment."
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>
      </div>
    )
  }

  return (
    <>
      <LiveDrawCard
        draw={mainDraw}
        gameType={gameType}
        onLoginClick={onLoginClick}
        onBuyNowClick={() => purchaseNavigate('/purchase')}
        onShareClick={() => setShareModalOpen(true)}
        hasAdditionalDraws={bonusDraws?.length > 0}
        editMode={editMode}
        onTranslationUpdate={handleTranslationUpdate}
      />
      <ShareModal isOpen={shareModalOpen} toggle={() => setShareModalOpen(false)} />
    </>
  )
}
